import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import TextInputField from '@/shared/ui/TextInputField/TextInputField'
import { PasswordStrengthBox } from './PasswordStrengthBox'
import cls from './password-with-retype.module.scss'

export const PasswordWithRetype = forwardRef((props, ref) => {
  const { value, data = {}, className, errorText, onFocus, innerRef } = props
  const [areMatching, setAreMatching] = useState(true)
  const [isPasswordInvalid, setIsPasswordInvalid] = useState(false)
  const [isPatternsInvalid, setPatternsInvalid] = useState(false)
  const [currentValue, setCurrentValue] = useState(undefined)

  const passwordRef = useRef(null)
  const retypeRef = useRef(null)

  const { retypeData = {}, mismatchErrorText } = data.custom || {}
  const { passwordStrength = {} } = data

  const areNotMatching = areMatching === false

  const getValue = useCallback(() => {
    const nextValue = passwordRef.current?.getValue?.()
    if (!nextValue) return undefined
    return nextValue
  }, [])

  const testPassword = useCallback((passwordPatterns, password) => {
    const requiredPatterns = passwordPatterns?.required || []
    const optionalPatterns = passwordPatterns?.optional?.patterns || []
    let minOptionalMatches = passwordPatterns?.optional?.min

    if (requiredPatterns.length) {
      const hasAllRequired = requiredPatterns.every(pattern => new RegExp(pattern).test(password))
      if (!hasAllRequired) return false
    }

    if (optionalPatterns.length) {
      if (minOptionalMatches === undefined) {
        minOptionalMatches = optionalPatterns.length - 1
      }

      let score = 0
      optionalPatterns.forEach(pattern => {
        if (new RegExp(pattern).test(password)) score += 1
      })

      return score >= minOptionalMatches
    }

    return true
  }, [])

  const handleChange = useCallback(() => {
    if (!passwordRef.current?.checkValidity?.()) {
      setIsPasswordInvalid(true)
      setAreMatching(true)
      return false
    }

    const passwordValue = passwordRef.current?.getValue?.()
    const retypedValue = retypeRef.current?.getValue?.()
    const matching = (!passwordValue && !retypedValue) || passwordValue === retypedValue

    setAreMatching(matching)
    setIsPasswordInvalid(false)

    return matching
  }, [])

  const handleValueChange = useCallback(event => {
    setCurrentValue(event?.target?.value)
  }, [])

  // const handleBlur = useCallback(() => {
  //   handleChange()
  // }, [handleChange])

  const handleFocus = useCallback(
    event => {
      onFocus && onFocus()
      setAreMatching(true)
    },
    [onFocus],
  )

  const checkValidity = useCallback(() => {
    if (!passwordRef.current?.checkValidity?.()) {
      setIsPasswordInvalid(true)
      return false
    }

    const { required } = data
    const passwordValue = getValue()

    if (required && !testPassword(passwordStrength.patterns, passwordValue)) {
      setIsPasswordInvalid(true)
      setPatternsInvalid(true)
      return false
    }

    const retypedValue = retypeRef.current?.getValue?.()
    if (required && !retypedValue) {
      setAreMatching(false)
      setIsPasswordInvalid(false)
      return false
    }

    const matching = (!passwordValue && !retypedValue) || passwordValue === retypedValue
    setAreMatching(matching)
    setIsPasswordInvalid(false)

    return matching
  }, [data, getValue, passwordStrength.patterns, testPassword])

  const blur = useCallback(() => {
    passwordRef.current?.blur?.()
    retypeRef.current?.blur?.()
  }, [])

  const imperativeApi = useMemo(
    () => ({
      blur,
      checkValidity,
      getValue,
    }),
    [blur, checkValidity, getValue],
  )

  useImperativeHandle(ref, () => imperativeApi, [imperativeApi])
  useImperativeHandle(innerRef, () => imperativeApi, [imperativeApi])

  return (
    <div className={className}>
      <TextInputField
        className={cls.password}
        innerRef={node => (passwordRef.current = node)}
        fullWidth
        tabIndex={1}
        data={{
          ...data,
          type: 'password',
          custom: undefined,
          ...(errorText ? { errorText } : {}),
        }}
        invalid={isPasswordInvalid || !!errorText}
        value={value}
        onFocus={handleFocus}
        onChange={handleValueChange}
        errorText={isPatternsInvalid && mismatchErrorText}
      />
      <TextInputField
        fullWidth
        tabIndex={2}
        innerRef={node => (retypeRef.current = node)}
        data={{
          ...retypeData,
          type: 'password',
        }}
        invalid={areNotMatching}
        onBlur={handleChange}
        onFocus={handleFocus}
      />
      <PasswordStrengthBox
        {...props}
        className={`${props.className || ''} ${cls.strengthBox}`.trim()}
        password={currentValue}
        patterns={passwordStrength.patterns}
      />
    </div>
  )
})

export default PasswordWithRetype
